import * as React from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import DOMPurify from 'dompurify'
import {
  ArrowUpRight,
  CheckCircle2,
  Copy,
  Download,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  Send,
  Share2,
  Workflow,
} from 'lucide-react'
import { toast } from 'sonner'

import { useQuery } from '@connectrpc/connect-query'
import { getQuoteDetail } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { DuplicateQuoteRequestSchema, GenerateQuotePdfRequestSchema, GetQuoteDetailRequestSchema, UpdateQuoteRequestSchema, type QuoteEmailOverride } from '@/gen/cortado/v1/cortado_pb'
import { GeneratePortalLinkRequestSchema } from '@/gen/cortado/v1/inquiries_pb'
import { ParentTypeEnum, QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import type { QuoteEvent } from '@/gen/cortado/v1/invoices_pb'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { usePortalLinkCopy } from '@/hooks/use-portal-link-copy'
import { usePermissions } from '@/hooks/use-permissions'
import { getErrorDescription } from '@/lib/error-utils'
import { captureError } from '@/lib/sentry'
import { formatDateTime, formatTimestamp } from '@/lib/date-utils'
import { formatDuration, formatFullName } from '@/lib/utils'
import { generateAndDownloadFile } from '@/lib/download-file'
import { useContact } from '@/stores/contacts.store'
import { useCurrentOrganization } from '@/stores/organization.store'
import { useAcceptQuoteAsAdmin, useGeneratePortalLink, useInquiry } from '@/stores/leads.store'
import { useNotes } from '@/stores/notes.store'
import { useAttachments } from '@/stores/attachments.store'
import { getQuoteDetailQueryOptions, useContactQuoteInteractions, useDuplicateQuote, useGenerateQuotePdf, useQuote, useUpdateQuote } from '@/stores/quotes.store'
import { useQuoteSend } from '@/hooks/use-quote-send'
import { useStripeConnection } from '@/stores/stripe.store'
import { useTasksList } from '@/stores/tasks.store'
import { seedQuoteDetailCaches } from '@/lib/detail-seeding'
import { connectTransport } from '@/lib/connect-transport'
import { isValidUUID } from '@/lib/uuid'
import { canPreloadOrganizationQueries } from '@/lib/route-preloading'

import { QuoteBuilderInline } from '@/components/quotes'
import { QuoteSendDialog } from '@/components/quotes/quote-send-dialog'
import { QuoteDetailErrorState } from '@/components/quotes/quote-detail-error-state'
import { QuoteStatusDropdown } from '@/components/leads/id/quote-status-dropdown'
import { ContactHeader } from '@/components/shared/contact-header'
import { ActivityTabs } from '@/components/shared/activity-tabs'
import { RunWorkflowDialog } from '@/components/shared/run-workflow-dialog'
import { ActionGate } from '@/components/shared/action-gate'
import {
  DocumentLineItemsCard,
  DocumentPaymentScheduleCard,
  DocumentStatusBanner,
  DocumentSummaryCard,
  LinkedRecordCard,
} from '@/components/shared/financial-document'
import { ConvertedRecordBanner } from '@/components/shared/converted-record-banner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SkeletonBar } from '@/components/shared/skeleton-bar'

export const Route = createFileRoute('/_app/_home/quotes/$id')({
  component: QuoteDetailPage,
  loader: async ({ context, params }) => {
    if (!isValidUUID(params.id) || !canPreloadOrganizationQueries(context.auth)) return
    const { queryClient } = context

    // Resolve the aggregate before mounting entity hooks so cold navigation
    // cannot race the individual quote/inquiry/contact/booking queries.
    const opts = getQuoteDetailQueryOptions(params.id)
    try {
      const resp = await queryClient.ensureQueryData(opts)
      const updatedAt = queryClient.getQueryState(opts.queryKey)?.dataUpdatedAt ?? Date.now()
      seedQuoteDetailCaches(queryClient, connectTransport, resp, updatedAt)
    } catch {
      // The component's entity queries provide the user-visible error state.
    }
  },
})

function timestampMillis(timestamp?: { seconds: bigint | number, nanos?: number }) {
  if (!timestamp) {
    return 0
  }

  const seconds = typeof timestamp.seconds === 'bigint'
    ? Number(timestamp.seconds)
    : timestamp.seconds

  return seconds * 1000 + Math.floor((timestamp.nanos ?? 0) / 1_000_000)
}

function getQuotePortalVisibilityMessage(status: QuoteStatusEnum) {
  switch (status) {
    case QuoteStatusEnum.QUOTE_STATUS_DRAFT:
      return 'Draft quotes are hidden from clients. Publish and send the portal link when this quote is ready.'
    case QuoteStatusEnum.QUOTE_STATUS_SENT:
      return 'This quote is live in the client portal. The portal link opens the lead portal, where clients can view any live quotes they have access to.'
    case QuoteStatusEnum.QUOTE_STATUS_VIEWED:
      return 'This quote is live in the client portal and has been viewed. The portal link opens the lead portal, not only this quote.'
    case QuoteStatusEnum.QUOTE_STATUS_REVISED:
      return 'This quote has a newer revision. Confirm the current version before sending the client portal link.'
    case QuoteStatusEnum.QUOTE_STATUS_ACCEPTED:
      return 'This quote has been accepted. The portal link still opens the lead portal for any live documents.'
    case QuoteStatusEnum.QUOTE_STATUS_CANCELLED:
    case QuoteStatusEnum.QUOTE_STATUS_EXPIRED:
    case QuoteStatusEnum.QUOTE_STATUS_REJECTED:
      return 'This quote is inactive and is not intended for client review. The portal link opens the lead portal for any other live documents.'
    default:
      return 'The portal link opens the lead portal, where clients can view any live quotes they have access to.'
  }
}

export function QuoteDetailPage() {
  const { id } = Route.useParams()
  useBreadcrumbFromRoute()

  const [editSheetOpen, setEditSheetOpen] = React.useState(false)
  const [confirmEditOpen, setConfirmEditOpen] = React.useState(false)

  const { quote, isLoading: quoteLoading, isError, error } = useQuote(id)
  const { inquiry } = useInquiry(quote?.inquiryId)
  const { contact } = useContact(quote?.contactId)

  // The converted invoice is resolved by the GetQuoteDetail aggregate (a pure
  // reverse-FK lookup on the backend), so read it straight from that query
  // instead of chaining quote -> booking -> invoice-lites here. The loader warms
  // this cache; on a cold mount this issues a normal fetch.
  const { data: sourceInvoice } = useQuery(
    getQuoteDetail,
    create(GetQuoteDetailRequestSchema, { id }),
    { enabled: !!id, select: (d) => d.convertedInvoice },
  )

  // Once a quote has been converted to an invoice, the invoice owns the client's
  // payment schedule — edits here never reach them. Warn before opening the editor
  // rather than letting the edit silently go nowhere.
  const requestEdit = React.useCallback(() => {
    if (sourceInvoice) {
      setConfirmEditOpen(true)
      return
    }
    setEditSheetOpen(true)
  }, [sourceInvoice])

  const { notes } = useNotes({
    parentType: ParentTypeEnum.PARENT_TYPE_QUOTE,
    parentId: id,
  })

  const { tasks } = useTasksList({
    parentType: ParentTypeEnum.PARENT_TYPE_QUOTE,
    parentId: id,
  })

  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_QUOTE,
    parentId: id,
  })
  const {
    interactions: quoteInteractions,
    isLoading: quoteInteractionsLoading,
  } = useContactQuoteInteractions({
    contactId: quote?.contactId,
    quoteId: quote?.id,
    limit: 100,
  })

  const quoteEngagement = React.useMemo(() => {
    return quoteInteractions.reduce<{
      totalViews: number
      lastViewedAt: typeof quoteInteractions[number]['lastViewedAt']
      lastViewedAtMillis: number
    }>((summary, interaction) => {
      const lastViewedAtMillis = timestampMillis(interaction.lastViewedAt)

      return {
        totalViews: summary.totalViews + interaction.viewCount,
        lastViewedAt: lastViewedAtMillis > summary.lastViewedAtMillis
          ? interaction.lastViewedAt
          : summary.lastViewedAt,
        lastViewedAtMillis: Math.max(summary.lastViewedAtMillis, lastViewedAtMillis),
      }
    }, {
      totalViews: 0,
      lastViewedAt: undefined,
      lastViewedAtMillis: 0,
    })
  }, [quoteInteractions])

  // Gate the full-page skeleton on the quote alone. The inquiry- and
  // contact-dependent sections (View Lead button, linked records, edit sheet,
  // contact header) all degrade gracefully while those resolve — and after the
  // loader's aggregate seeding they are cache-hits anyway.
  const isLoading = quoteLoading

  if (isLoading) {
    return <QuoteDetailSkeleton />
  }

  if (isError || !quote) {
    return <QuoteDetailErrorState error={error} />
  }

  const contactName = formatFullName(contact?.firstName, contact?.lastName, 'Unknown contact')
  const isAccepted = quote.status === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED

  return (
    <div className="flex h-full flex-col gap-4 p-4 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <ContactHeader
          firstName={contact?.firstName}
          lastName={contact?.lastName}
          email={contact?.email}
          phoneNumber={contact?.phoneNumber}
          isArchived={!!contact?.archivedAt}
        />
        {inquiry && (
          <Button variant="outline" size="sm" asChild>
            <Link to="/leads/$id" params={{ id: inquiry.id }}>
              View Lead
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </Button>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <main className="space-y-4">
          <DocumentStatusBanner
            kind="quote"
            status={quote.status}
            title={`Quote #${quote.quoteNumber}`}
            detail={quote.title || undefined}
          />

          {sourceInvoice && (
            <ConvertedRecordBanner
              description={`This quote was converted to Invoice #${sourceInvoice.invoiceNumber}. The client now pays from that invoice, so payment schedules and pricing changed here won't reach them.`}
              action={
                <Link
                  to="/invoices/$id"
                  params={{ id: sourceInvoice.id }}
                  className="font-medium underline underline-offset-2 hover:text-yellow-900 dark:hover:text-yellow-100"
                >
                  Edit Invoice #{sourceInvoice.invoiceNumber} →
                </Link>
              }
            />
          )}

          {quote.description && <DescriptionCard html={quote.description} />}

          <DocumentLineItemsCard
            kind="quote"
            items={quote.lineItems}
            subtotalCents={Number(quote.subtotalCents || 0n)}
            discountCents={Number(quote.discountCents || 0n)}
            taxCents={Number(quote.salesTaxCents || 0n)}
            tipCents={Number(quote.enforcedTipCents || 0n)}
            totalCents={Number(quote.totalCents || 0n)}
            isEditable={!isAccepted}
            onEditClick={requestEdit}
          />

          <DocumentPaymentScheduleCard schedules={quote.paymentSchedules || []} />

          <QuoteEventsCard events={quote.quoteEvents || []} />

          <ActivityTabs
            organizationId={quote.organizationId}
            parentType={ParentTypeEnum.PARENT_TYPE_QUOTE}
            parentId={quote.id}
            notes={notes}
            tasks={tasks}
            attachments={attachments}
            customerEmail={contact?.email}
            emailContactId={quote.contactId}
          />
        </main>

        <aside className="space-y-4">
          <DocumentSummaryCard
            kind="quote"
            status={quote.status}
            numberLabel={`#${quote.quoteNumber}`}
            title={quote.title || contactName}
            totalCents={Number(quote.totalCents || 0n)}
            subtotalCents={Number(quote.subtotalCents || 0n)}
            discountCents={Number(quote.discountCents || 0n)}
            taxCents={Number(quote.salesTaxCents || 0n)}
            tipCents={Number(quote.enforcedTipCents || 0n)}
            createdAt={quote.createdAt}
            sentAt={quote.sentAt}
            viewedAt={quote.viewedAt}
            acceptedAt={quote.acceptedAt}
            validUntil={quote.validUntil}
          />

          <QuoteQuickActions
            quoteId={quote.id}
            inquiryId={quote.inquiryId}
            status={quote.status}
            recipientName={contactName}
            hasRecipient={Boolean(contact?.id)}
            recipientEmail={contact?.email}
            isPayable={Number(quote.totalCents || 0n) > 0}
            onEdit={requestEdit}
          />

          <QuoteEngagementCard
            totalViews={quoteEngagement.totalViews}
            lastViewedAt={quoteEngagement.lastViewedAt}
            isLoading={quoteInteractionsLoading}
          />

          <LinkedRecordCard
            title="Linked Records"
            rows={[
              {
                label: 'Lead',
                value: inquiry ? contactName : 'Loading lead',
                action: inquiry ? (
                  <Button variant="ghost" size="icon" asChild>
                    <Link to="/leads/$id" params={{ id: inquiry.id }} aria-label="Open linked lead">
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                ) : undefined,
              },
              {
                label: 'Created Invoice',
                value: sourceInvoice ? `Invoice #${sourceInvoice.invoiceNumber}` : 'Not created yet',
                action: sourceInvoice ? (
                  <Button variant="ghost" size="icon" asChild>
                    <Link to="/invoices/$id" params={{ id: sourceInvoice.id }} aria-label="Open created invoice">
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </Button>
                ) : undefined,
              },
            ]}
          />
        </aside>
      </div>

      <AlertDialog open={confirmEditOpen} onOpenChange={setConfirmEditOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>This quote is already an invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Quote #{quote.quoteNumber} was converted to Invoice #{sourceInvoice?.invoiceNumber}.
              The client pays from that invoice, so deposits, payment schedules, and pricing you
              change here will not reach them. Edit the invoice instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {sourceInvoice && (
              <Button variant="outline" asChild>
                <Link to="/invoices/$id" params={{ id: sourceInvoice.id }}>
                  Edit Invoice #{sourceInvoice.invoiceNumber}
                </Link>
              </Button>
            )}
            <AlertDialogAction
              onClick={() => {
                // Defer so this dialog unmounts before the edit sheet mounts. Two
                // Radix modals transitioning in the same tick can leave the body
                // scroll-locked (see frontend-patterns rule).
                setTimeout(() => setEditSheetOpen(true), 0)
              }}
            >
              Edit quote anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
        <SheetContent className="sm:max-w-5xl">
          <SheetHeader>
            <SheetTitle>Edit Quote</SheetTitle>
          </SheetHeader>
          <SheetBody>
            {inquiry ? (
              <QuoteBuilderInline
                inquiry={inquiry}
                selectedQuoteId={quote.id}
                onCancel={() => setEditSheetOpen(false)}
                onSuccess={() => setEditSheetOpen(false)}
              />
            ) : (
              <Empty className="border border-dashed py-12">
                <EmptyMedia variant="icon">
                  <FileText className="h-6 w-6" />
                </EmptyMedia>
                <EmptyDescription>Load the linked lead before editing this quote.</EmptyDescription>
              </Empty>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function QuoteEngagementCard({
  totalViews,
  lastViewedAt,
  isLoading,
}: {
  totalViews: number
  lastViewedAt?: { seconds: bigint | number, nanos?: number }
  isLoading: boolean
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Eye className="h-4 w-4" />
          Engagement
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Views</span>
          {isLoading ? (
            <SkeletonBar className="h-4 w-10" />
          ) : (
            <span className="font-medium tabular-nums">{totalViews}</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Last viewed</span>
          {isLoading ? (
            <SkeletonBar className="h-4 w-24" />
          ) : (
            <span className="text-right font-medium">
              {lastViewedAt ? formatTimestamp(lastViewedAt, 'MMM d, yyyy h:mm a') : '-'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function QuoteQuickActions({
  quoteId,
  inquiryId,
  status,
  recipientName,
  hasRecipient,
  recipientEmail,
  isPayable,
  onEdit,
}: {
  quoteId: string
  inquiryId: string
  status: QuoteStatusEnum
  recipientName: string
  hasRecipient: boolean
  recipientEmail?: string
  /** Drives the Stripe gate, which used to exist only in the quote builder. */
  isPayable: boolean
  onEdit: () => void
}) {
  const navigate = useNavigate()
  const [isRunWorkflowOpen, setIsRunWorkflowOpen] = React.useState(false)
  const { currentOrganization } = useCurrentOrganization()
  const [showQuoteSendDialog, setShowQuoteSendDialog] = React.useState(false)
  const organizationId = currentOrganization?.id || ''
  const { can } = usePermissions(organizationId)
  const canRunWorkflow = can('AutomationsManage')
  const { isConnected: isStripeConnected, hasLoaded: stripeHasLoaded } = useStripeConnection(organizationId)
  // The Stripe gate used to live only in the quote builder, so the detail page
  // and the leads card could send a payable quote the client could never pay.
  const stripeBlocksSend = stripeHasLoaded && isPayable && !isStripeConnected
  const { updateQuote, isLoading: isPublishingQuote } = useUpdateQuote()
  const isDraftStatus = status === QuoteStatusEnum.QUOTE_STATUS_DRAFT
  const { sendQuote, isSending: isSendingQuote } = useQuoteSend({ isDraft: isDraftStatus })
  const { duplicateQuote, isLoading: isDuplicatingQuote } = useDuplicateQuote()
  const { generateLink } = useGeneratePortalLink()
  const { acceptQuote, isLoading: isAcceptingQuote } = useAcceptQuoteAsAdmin()
  const { generatePdf, isLoading: isGeneratingPdf } = useGenerateQuotePdf()

  const isDraft = status === QuoteStatusEnum.QUOTE_STATUS_DRAFT
  const isAccepted = status === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED
  const isInactive =
    status === QuoteStatusEnum.QUOTE_STATUS_CANCELLED ||
    status === QuoteStatusEnum.QUOTE_STATUS_EXPIRED ||
    status === QuoteStatusEnum.QUOTE_STATUS_REJECTED
  const portalVisibilityMessage = getQuotePortalVisibilityMessage(status)
  const sendActionPending = isSendingQuote || isPublishingQuote

  const handleSendQuote = async (input: {
    additionalRecipientEmails: string[]
    emailOverride: QuoteEmailOverride
  }) => sendQuote({ quoteId, ...input })

  const handleOpenSendDialog = () => {
    if (!recipientEmail?.trim()) {
      toast.error('Add an email address to the quote contact before sending.')
      return
    }
    setShowQuoteSendDialog(true)
  }

  const handlePublishOnly = async () => {
    try {
      await updateQuote(
        create(UpdateQuoteRequestSchema, {
          id: quoteId,
          status: QuoteStatusEnum.QUOTE_STATUS_SENT,
          updateMask: create(FieldMaskSchema, { paths: ['status'] }),
        })
      )
      toast.success('Quote is live in the client portal')
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-detail-publish' })
      toast.error('Failed to publish quote', {
        description: getErrorDescription(error),
      })
    }
  }

  const handleDuplicateQuote = async () => {
    try {
      const response = await duplicateQuote(create(DuplicateQuoteRequestSchema, { id: quoteId }))
      if (response.quote?.id) {
        toast.success('Quote duplicated successfully')
        navigate({ to: '/quotes/$id', params: { id: response.quote.id } })
      }
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-detail-duplicate' })
      toast.error('Failed to duplicate quote', {
        description: getErrorDescription(error),
      })
    }
  }

  const handleDownloadPdf = async () => {
    try {
      await generateAndDownloadFile(
        () => generatePdf(create(GenerateQuotePdfRequestSchema, { quoteId })),
        'Quote.pdf'
      )
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-detail-download-pdf' })
      toast.error('Failed to download quote PDF', {
        description: getErrorDescription(error),
      })
    }
  }

  const { copyRecipientLink, copyGenericLink, isCopying, isCopyingRecipientLink, isCopyingGenericLink } = usePortalLinkCopy(
    (attributionMode) => generateLink(create(GeneratePortalLinkRequestSchema, {
      inquiryId,
      quoteId,
      attributionMode,
    })),
    (error) => {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-detail-share' })
      toast.error('Failed to generate portal link', {
        description: getErrorDescription(error),
      })
    }
  )

  const handleAcceptQuote = async () => {
    try {
      const response = await acceptQuote(inquiryId, quoteId)
      if (response.invoiceId) {
        toast.success('Quote accepted and invoice created')
        navigate({ to: '/invoices/$id', params: { id: response.invoiceId } })
        return
      }
      toast.success('Quote accepted')
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-detail-accept-admin' })
      toast.error('Failed to accept quote', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Client portal visibility</p>
          <p className="mt-1 leading-relaxed">{portalVisibilityMessage}</p>
        </div>

        {!isAccepted && !isInactive && (
          <Button
            type="button"
            className="w-full justify-start gap-2"
            onClick={handleOpenSendDialog}
            disabled={sendActionPending || stripeBlocksSend}
          >
            {isSendingQuote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {isDraft ? 'Publish & Send Link' : 'Send Portal Link'}
          </Button>
        )}

        {isDraft && (
          <Button
            type="button"
            variant="secondary"
            className="w-full justify-start gap-2"
            onClick={handlePublishOnly}
            disabled={isPublishingQuote || isSendingQuote}
          >
            {isPublishingQuote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            {isPublishingQuote ? 'Publishing...' : 'Publish Only'}
          </Button>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            {hasRecipient && (
              <Button type="button" variant="outline" size="sm" className="justify-start gap-2" onClick={copyRecipientLink} disabled={isCopying}>
                {isCopyingRecipientLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                {isCopyingRecipientLink ? 'Generating...' : `Copy link for ${recipientName || 'contact'}`}
              </Button>
            )}
            <button
              type="button"
              onClick={copyGenericLink}
              disabled={isCopying}
              className="text-xs text-muted-foreground text-left hover:underline disabled:opacity-50"
            >
              {isCopyingGenericLink ? 'Generating...' : 'Copy generic link'}
            </button>
          </div>
          <Button type="button" variant="outline" size="sm" className="justify-start gap-2" onClick={handleDuplicateQuote} disabled={isDuplicatingQuote}>
            {isDuplicatingQuote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
            Duplicate
          </Button>
          {!isAccepted && !isInactive && (
            <Button type="button" variant="outline" size="sm" className="justify-start gap-2" onClick={onEdit}>
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          {!isAccepted && !isInactive && (
            <Button type="button" variant="outline" size="sm" className="justify-start gap-2" onClick={handleAcceptQuote} disabled={isAcceptingQuote}>
              {isAcceptingQuote ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Accept
            </Button>
          )}
          {!isDraft && (
            <Button type="button" variant="outline" size="sm" className="justify-start gap-2" onClick={handleDownloadPdf} disabled={isGeneratingPdf}>
              {isGeneratingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {isGeneratingPdf ? 'Downloading...' : 'PDF'}
            </Button>
          )}
          <ActionGate
            organizationId={organizationId}
            permission="AutomationsManage"
            verb="run"
            noun="workflows"
          >
            <Button type="button" variant="outline" size="sm" className="justify-start gap-2" onClick={() => setIsRunWorkflowOpen(true)}>
              <Workflow className="h-4 w-4" />
              Run Workflow
            </Button>
          </ActionGate>
        </div>

        <Separator className="my-3" />

        <div>
          <QuoteStatusDropdown quoteId={quoteId} currentStatus={status} className="w-full justify-center text-sm" />
        </div>
      </CardContent>

      {canRunWorkflow && (
        <RunWorkflowDialog
          sourceResourceId={quoteId}
          triggerEntityType="QUOTE"
          open={isRunWorkflowOpen}
          onOpenChange={setIsRunWorkflowOpen}
        />
      )}

      <QuoteSendDialog
        key={quoteId}
        open={showQuoteSendDialog}
        onOpenChange={setShowQuoteSendDialog}
        organizationId={organizationId}
        quoteId={quoteId}
        isDraft={isDraft}
        recipientName={recipientName}
        recipientEmail={recipientEmail}
        isSending={isSendingQuote}
        stripeBlocksSend={stripeBlocksSend}
        onSend={handleSendQuote}
      />
    </Card>
  )
}

function DescriptionCard({ html }: { html: string }) {
  const cleanHtml = React.useMemo(() => DOMPurify.sanitize(html), [html])

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Description</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="prose prose-sm max-w-none text-sm dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      </CardContent>
    </Card>
  )
}

function QuoteEventsCard({ events }: { events: QuoteEvent[] }) {
  const sortedEvents = React.useMemo(
    () => [...events].sort((a, b) => Number(a.eventDate?.seconds || 0n) - Number(b.eventDate?.seconds || 0n)),
    [events],
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Quote Events</CardTitle>
      </CardHeader>
      <CardContent>
        {sortedEvents.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <FileText className="mx-auto mb-2 h-10 w-10 opacity-50" />
            <p>No event details on this quote</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {sortedEvents.map((event, index) => (
              <div key={event.id || index} className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs font-medium text-muted-foreground">Event {index + 1}</p>
                <p className="mt-1 text-sm font-medium">
                  {event.eventDate ? formatDateTime(event.eventDate, event.eventTimezone) : 'Date not set'}
                </p>
                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                  {event.eventLength ? <p>{formatDuration(event.eventLength, 'long')}</p> : null}
                  {event.eventGuests ? <p>{event.eventGuests} guests</p> : null}
                  {event.eventLocation ? <p className="line-clamp-2">{event.eventLocation}</p> : null}
                  {event.baristaCount || event.cartCount ? (
                    <p>
                      {event.baristaCount ?? 0} baristas / {event.cartCount ?? 0} carts
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function QuoteDetailSkeleton() {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <SkeletonBar className="h-8 w-48" />
          <SkeletonBar className="h-4 w-64" />
        </div>
        <SkeletonBar className="h-9 w-28" />
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-4">
          <SkeletonBar className="h-24 w-full rounded-lg" />
          <SkeletonBar className="h-80 w-full rounded-lg" />
          <SkeletonBar className="h-56 w-full rounded-lg" />
        </div>
        <div className="space-y-4">
          <SkeletonBar className="h-80 w-full rounded-lg" />
          <SkeletonBar className="h-48 w-full rounded-lg" />
        </div>
      </div>
    </div>
  )
}
